import React, { useState, useEffect, useCallback } from 'react';
import WineAutocomplete from './WineAutocomplete';
import AttachmentPicker from './AttachmentPicker';
import LabelScanner from './LabelScanner';
import { applyScanToWineForm, uploadWineLabel } from './labelScan';
import { useI18n } from './i18n';
import { exportPdf } from './pdfExport';
import { COLOR_HUES, BUBBLE_GRAINS, PERLAGE_PERSISTENCES, EVOLUTION_STATES, WINE_TYPES, CLASSIFICATIONS } from './codes';

// v2 drafts use their own key: drafts saved by older versions (Italian field names) are simply ignored.
const DRAFT_KEY = 'tnh2_draft_aspi';

// i18n keys: aspi.p.<field> (label), aspi.scale.<field>.<1..5> (scale labels), aspi.def.<field> ("?" definition).
// Stored values are stable codes (codes.js): texts are translated at render time with tc().

const INITIAL_STATE = {
  wine_name: '',
  classification: '',
  wine_type: '',
  vintage: '',
  producer: '',
  production_area: '',
  tasting_date: new Date().toISOString().slice(0, 10),
  taster: '',
  tasting_temperature: '',
  clarity: 3,
  transparency: null,           // optional 1-5 marks stay null until the user rates them
  visual_reflections: '',
  legs: '',
  color_hue: '',
  color_intensity: 3,
  consistency: 3,
  bubble_grain: '',
  perlage_persistence: '',
  olfactory_intensity: 3,
  olfactory_complexity: 3,
  olfactory_quality: 3,
  olfactory_description: '',
  olfactory_recognition: '',
  gustatory_sugars: 1,
  gustatory_alcohols: 3,
  gustatory_polyols: 3,
  gustatory_acids: 3,
  gustatory_tannins: 3,
  gustatory_minerals: 3,
  gustatory_balance: 3,
  gustatory_intensity: 3,
  gustatory_persistence: 3,
  gustatory_quality: 3,
  evolution_state: 'ready',
  harmony: 3,
  aging_potential: '',
  grape_identification: '',
  pairing_suggestion: '',
  serving_temperature: '',
  glass_shape: '',
  value_for_money: null,
  final_score: '',
  notes: '',
  visibility: 'private',
};

const WINE_FAMILIES = ['red', 'white', 'rose', 'sparkling'];
// Wine type of the header ("Tipologia") -> family that drives the colour chips and the effervescence block
const FAMILY_OF_TYPE = { red: 'red', white: 'white', rose: 'rose', passito: 'white', semi_sparkling: 'sparkling', sparkling: 'sparkling' };
const TEXT_FIELDS = ['visual_reflections', 'legs', 'olfactory_recognition', 'olfactory_description', 'aging_potential',
  'grape_identification', 'pairing_suggestion', 'glass_shape'];
const TEMPERATURE_FIELDS = ['tasting_temperature', 'serving_temperature'];

function loadDraft() {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    if (saved) {
      const known = {};
      Object.keys(INITIAL_STATE).forEach((k) => { if (saved[k] !== undefined) known[k] = saved[k]; });
      return { ...INITIAL_STATE, ...known };
    }
  } catch { /* ignore */ }
  return INITIAL_STATE;
}

// Form state from an existing sheet (edit mode); null values keep the form default, except the date.
function fromSheet(sheet) {
  const out = { ...INITIAL_STATE, tasting_date: '' };
  Object.keys(INITIAL_STATE).forEach((k) => {
    if (sheet[k] !== undefined && sheet[k] !== null) out[k] = sheet[k];
  });
  return out;
}

// Wine family shown by the form (UI only, not stored), deduced from the values already present
function deduceWineFamily(f) {
  if (FAMILY_OF_TYPE[f.wine_type]) return FAMILY_OF_TYPE[f.wine_type];
  if (f.bubble_grain || f.perlage_persistence) return 'sparkling';
  if (COLOR_HUES.white.includes(f.color_hue)) return 'white';
  if (COLOR_HUES.rose.includes(f.color_hue)) return 'rose';
  return 'red';
}

function SliderField({ name, value, onChange }) {
  const { t } = useI18n();
  const [showInfo, setShowInfo] = useState(false);
  const definition = t(`aspi.def.${name}`);
  const scale = [1, 2, 3, 4, 5].map((n) => t(`aspi.scale.${name}.${n}`));
  return (
    <div className="slider-field">
      <label>
        <span className="field-label">{t(`aspi.p.${name}`)}</span>
        <span className="field-value">{scale[value - 1]}</span>
        <button type="button" className="info-btn"
          onClick={(e) => { e.preventDefault(); setShowInfo(!showInfo); }}
          title={t('aspi.how_to')}>?</button>
      </label>
      <input
        type="range"
        min={1}
        max={5}
        value={value}
        onChange={(e) => onChange(name, parseInt(e.target.value, 10))}
      />
      <div className="slider-labels">
        {scale.map((label, i) => (
          <span key={i} className={value === i + 1 ? 'active' : ''}>{label}</span>
        ))}
      </div>
      {showInfo && <div className="info-box">{definition}</div>}
    </div>
  );
}

// Optional 1-5 mark (null = not rated): the slider shows the neutral middle until the user moves it
function OptionalSliderField({ name, value, onChange }) {
  const { t } = useI18n();
  const rated = value !== null && value !== undefined && value !== '';
  return (
    <div className={rated ? '' : 'slider-unrated'} style={rated ? undefined : { opacity: 0.75 }}>
      <SliderField name={name} value={rated ? value : 3} onChange={onChange} />
      <small className="import-hint">
        {rated
          ? <button type="button" className="chip" onClick={() => onChange(name, null)}>{t('aspi.clear_rating')}</button>
          : t('aspi.not_rated')}
      </small>
    </div>
  );
}

// ASPI evaluation sheet form. Without initialData it creates a sheet, with initialData it edits it.
// onSubmit(data, attachmentItems): attachments can only be chosen when creating (registered users).
function AspiForm({ onSubmit, initialData, onCancel }) {
  const { t, tc } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? fromSheet(initialData) : loadDraft()));
  const [wineFamily, setWineFamily] = useState(() => deduceWineFamily(form));
  const [attachments, setAttachments] = useState([]);
  const [labelPhoto, setLabelPhoto] = useState(null); // scanned label photo, uploaded to the wine catalog after saving

  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const handleChange = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleReset = () => {
    if (!window.confirm(t('aspi.confirm_reset'))) return;
    setForm(INITIAL_STATE);
    setAttachments([]);
    setLabelPhoto(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  // Sheet data in the API format (used both for saving and for the PDF export). Empty optional values are sent as null.
  const buildData = () => {
    if (!form.wine_name.trim()) {
      alert(t('aspi.alert_wine_name'));
      return null;
    }
    const sparkling = wineFamily === 'sparkling';
    const optionalTexts = Object.fromEntries(TEXT_FIELDS.map((k) => [k, (form[k] || '').trim() || null]));
    const temperatures = Object.fromEntries(TEMPERATURE_FIELDS.map((k) => [k, form[k] === '' || form[k] === null ? null : parseFloat(form[k])]));
    return {
      ...form,
      ...optionalTexts,
      ...temperatures,
      classification: form.classification || null,
      wine_type: form.wine_type || null,
      transparency: form.transparency ?? null,
      value_for_money: form.value_for_money ?? null,
      vintage: form.vintage ? parseInt(form.vintage, 10) : null,
      tasting_date: form.tasting_date || null,
      color_hue: form.color_hue || null,
      bubble_grain: sparkling ? (form.bubble_grain || null) : null,
      perlage_persistence: sparkling ? (form.perlage_persistence || null) : null,
      final_score: form.final_score === '' ? null : parseFloat(form.final_score),
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = buildData();
    if (!data) return;
    const photo = editing ? null : labelPhoto;
    Promise.resolve(onSubmit(data, editing ? [] : attachments)).then(() => uploadWineLabel({ name: data.wine_name, vintage: data.vintage, producer: data.producer }, photo));
    if (editing) return;
    setForm(INITIAL_STATE);
    setAttachments([]);
    setLabelPhoto(null);
    localStorage.removeItem(DRAFT_KEY);
  };

  const handleExportPdf = () => {
    const data = buildData();
    if (data) exportPdf({ path: 'aspi', data, errorMessage: t('ui.export_error') });
  };

  const hues = COLOR_HUES[wineFamily === 'sparkling' ? 'white' : wineFamily];

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      {/* ── GENERAL INFORMATION ── */}
      <fieldset>
        <legend>{t('aspi.sec.info')}</legend>
        <LabelScanner kind="wine" onPhoto={setLabelPhoto}
          onApply={(values, scan, options) => setForm((prev) => applyScanToWineForm(prev, values, options))} />
        <small className="import-hint">{t('aspi.official_note')}</small>
        <div className="form-grid">
          <div className="form-field">
            <label>{t('aspi.p.wine_name')} *</label>
            <WineAutocomplete
              value={form.wine_name}
              onChange={(v) => handleChange('wine_name', v)}
              onSelect={(wine) => setForm((prev) => ({
                ...prev,
                wine_name: wine.name,
                vintage: wine.vintage || '',
                producer: wine.producer || '',
                production_area: wine.production_area || prev.production_area,
                classification: wine.classification || prev.classification,
                wine_type: wine.wine_type || prev.wine_type,
              }))}
              required
            />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.classification')}</label>
            <select name="classification" value={form.classification} onChange={handleInputChange}>
              <option value="">—</option>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{tc('classification', c)}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>{t('aspi.p.wine_type')}</label>
            <div className="chip-group">
              {WINE_TYPES.map((type) => (
                <button type="button" key={type} className={`chip ${form.wine_type === type ? 'selected' : ''}`}
                  onClick={() => {
                    handleChange('wine_type', form.wine_type === type ? '' : type);
                    if (form.wine_type !== type && FAMILY_OF_TYPE[type] && FAMILY_OF_TYPE[type] !== wineFamily) {
                      setWineFamily(FAMILY_OF_TYPE[type]);
                      handleChange('color_hue', '');
                    }
                  }}>{tc('wine_type', type)}</button>
              ))}
            </div>
          </div>
          <div className="form-field">
            <label>{t('aspi.p.vintage')}</label>
            <input name="vintage" type="number" min={1900} max={2030} value={form.vintage} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.producer')}</label>
            <input name="producer" value={form.producer} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.production_area')}</label>
            <input name="production_area" value={form.production_area} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.taster')}</label>
            <input name="taster" value={form.taster} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.tasting_temperature')} (°C)</label>
            <input name="tasting_temperature" type="number" step={0.5} min={-5} max={40}
              value={form.tasting_temperature} onChange={handleInputChange} />
          </div>
        </div>
      </fieldset>

      {/* ── VISUAL EXAMINATION ── */}
      <fieldset>
        <legend>{t('aspi.sec.visual')}</legend>

        <div className="wine-type-selector">
          <label>{t('aspi.wine_type')}</label>
          <div className="radio-group">
            {WINE_FAMILIES.map((family) => (
              <label key={family} className={wineFamily === family ? 'selected' : ''}>
                <input type="radio" name="wineFamily" value={family} checked={wineFamily === family}
                  onChange={() => { setWineFamily(family); handleChange('color_hue', ''); }} />
                {tc('wine_type', family)}
              </label>
            ))}
          </div>
        </div>

        <SliderField name="clarity" value={form.clarity} onChange={handleChange} />
        <OptionalSliderField name="transparency" value={form.transparency} onChange={handleChange} />

        <div className="form-field">
          <label>{t('aspi.p.color_hue')}</label>
          <div className="chip-group">
            {hues.map((hue) => (
              <button type="button" key={hue}
                className={`chip ${form.color_hue === hue ? 'selected' : ''}`}
                onClick={() => handleChange('color_hue', hue)}>
                {tc('hue', hue)}
              </button>
            ))}
          </div>
        </div>

        <SliderField name="color_intensity" value={form.color_intensity} onChange={handleChange} />
        <div className="form-field">
          <label>{t('aspi.p.visual_reflections')}</label>
          <input name="visual_reflections" value={form.visual_reflections} onChange={handleInputChange}
            maxLength={200} placeholder={t('aspi.ph.visual_reflections')} />
        </div>
        <SliderField name="consistency" value={form.consistency} onChange={handleChange} />
        <div className="form-field">
          <label>{t('aspi.p.legs')}</label>
          <input name="legs" value={form.legs} onChange={handleInputChange}
            maxLength={100} placeholder={t('aspi.ph.legs')} />
        </div>

        {wineFamily === 'sparkling' && (
          <>
            <h4 className="section-subtitle">{t('aspi.effervescence.title')}</h4>
            <div className="form-field">
              <label>{t('aspi.p.bubble_grain')}</label>
              <div className="chip-group">
                {BUBBLE_GRAINS.map((grain) => (
                  <button type="button" key={grain}
                    className={`chip ${form.bubble_grain === grain ? 'selected' : ''}`}
                    onClick={() => handleChange('bubble_grain', grain)}>{tc('grain', grain)}</button>
                ))}
              </div>
            </div>
            <div className="form-field">
              <label>{t('aspi.p.perlage_persistence')}</label>
              <div className="chip-group">
                {PERLAGE_PERSISTENCES.map((persistence) => (
                  <button type="button" key={persistence}
                    className={`chip ${form.perlage_persistence === persistence ? 'selected' : ''}`}
                    onClick={() => handleChange('perlage_persistence', persistence)}>{tc('perlage', persistence)}</button>
                ))}
              </div>
            </div>
          </>
        )}
      </fieldset>

      {/* ── OLFACTORY EXAMINATION ── */}
      <fieldset>
        <legend>{t('aspi.sec.olfactory')}</legend>
        <SliderField name="olfactory_intensity" value={form.olfactory_intensity} onChange={handleChange} />
        <SliderField name="olfactory_complexity" value={form.olfactory_complexity} onChange={handleChange} />
        <SliderField name="olfactory_quality" value={form.olfactory_quality} onChange={handleChange} />
        <div className="form-field">
          <label>{t('aspi.p.olfactory_description')}</label>
          <textarea name="olfactory_description" value={form.olfactory_description} onChange={handleInputChange}
            placeholder={t('aspi.description_ph')} rows={3} />
        </div>
        <div className="form-field">
          <label>{t('aspi.p.olfactory_recognition')}</label>
          <textarea name="olfactory_recognition" value={form.olfactory_recognition} onChange={handleInputChange}
            maxLength={500} placeholder={t('aspi.ph.olfactory_recognition')} rows={2} />
        </div>
      </fieldset>

      {/* ── GUSTATORY EXAMINATION ── */}
      <fieldset>
        <legend>{t('aspi.sec.gustatory')}</legend>
        <h4 className="section-subtitle">{t('aspi.sub.soft')}</h4>
        <SliderField name="gustatory_sugars" value={form.gustatory_sugars} onChange={handleChange} />
        <SliderField name="gustatory_alcohols" value={form.gustatory_alcohols} onChange={handleChange} />
        <SliderField name="gustatory_polyols" value={form.gustatory_polyols} onChange={handleChange} />

        <h4 className="section-subtitle">{t('aspi.sub.hard')}</h4>
        <SliderField name="gustatory_acids" value={form.gustatory_acids} onChange={handleChange} />
        <SliderField name="gustatory_tannins" value={form.gustatory_tannins} onChange={handleChange} />
        <SliderField name="gustatory_minerals" value={form.gustatory_minerals} onChange={handleChange} />

        <h4 className="section-subtitle">{t('aspi.sub.body_finish')}</h4>
        <SliderField name="gustatory_balance" value={form.gustatory_balance} onChange={handleChange} />
        <SliderField name="gustatory_intensity" value={form.gustatory_intensity} onChange={handleChange} />
        <SliderField name="gustatory_persistence" value={form.gustatory_persistence} onChange={handleChange} />
        <SliderField name="gustatory_quality" value={form.gustatory_quality} onChange={handleChange} />
      </fieldset>

      {/* ── FINAL CONSIDERATIONS ── */}
      <fieldset>
        <legend>{t('aspi.sec.final')}</legend>

        <div className="form-field">
          <label>{t('aspi.p.evolution_state')}</label>
          <div className="chip-group">
            {EVOLUTION_STATES.map((state) => (
              <button type="button" key={state}
                className={`chip ${form.evolution_state === state ? 'selected' : ''}`}
                onClick={() => handleChange('evolution_state', state)}>
                {tc('evolution_state', state)}
              </button>
            ))}
          </div>
        </div>

        <SliderField name="harmony" value={form.harmony} onChange={handleChange} />

        <div className="form-grid">
          <div className="form-field">
            <label>{t('aspi.p.aging_potential')}</label>
            <input name="aging_potential" value={form.aging_potential} onChange={handleInputChange}
              maxLength={100} placeholder={t('aspi.ph.aging_potential')} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.grape_identification')}</label>
            <input name="grape_identification" value={form.grape_identification} onChange={handleInputChange}
              maxLength={200} placeholder={t('aspi.ph.grape_identification')} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.pairing_suggestion')}</label>
            <input name="pairing_suggestion" value={form.pairing_suggestion} onChange={handleInputChange}
              maxLength={300} placeholder={t('aspi.ph.pairing_suggestion')} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.serving_temperature')} (°C)</label>
            <input name="serving_temperature" type="number" step={0.5} min={-5} max={40}
              value={form.serving_temperature} onChange={handleInputChange} />
          </div>
          <div className="form-field">
            <label>{t('aspi.p.glass_shape')}</label>
            <input name="glass_shape" value={form.glass_shape} onChange={handleInputChange}
              maxLength={100} placeholder={t('aspi.ph.glass_shape')} />
          </div>
        </div>

        <OptionalSliderField name="value_for_money" value={form.value_for_money} onChange={handleChange} />

        <div className="form-grid">
          <div className="form-field">
            <label>{t('aspi.p.final_score')} (0-100)</label>
            <input name="final_score" type="number" min={0} max={100} step={0.5}
              value={form.final_score} onChange={handleInputChange} />
          </div>
        </div>

        <div className="form-field">
          <label>{t('aspi.p.notes')}</label>
          <textarea name="notes" value={form.notes} onChange={handleInputChange} rows={3}
            placeholder={t('aspi.notes_ph')} />
        </div>
      </fieldset>

      {/* ── ATTACHMENTS (creation only; registered users) ── */}
      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachments} onChange={setAttachments} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('aspi.btn.save_edit') : t('aspi.btn.save')}</button>
        <button type="button" className="reset-btn" onClick={handleExportPdf}>{t('ui.export_pdf')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default AspiForm;
